import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEditors } from './hooks/useEditors';
import { useSubscriptionCheck } from './hooks/useSubscriptionCheck';
import LoadingSpinner from './components/LoadingSpinner';
import Comments from './components/Comments';
import './EditorDatabase.css';

const EditorDatabase = () => {
  const navigate = useNavigate();
  const { subscriptionPlan, isLoading: subscriptionLoading } = useSubscriptionCheck(null);
  const [offset, setOffset] = useState(0);
  const [nameSearch, setNameSearch] = useState('');
  const [keywordSearch, setKeywordSearch] = useState('');
  const [publicationSearch, setPublicationSearch] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [expandedCards, setExpandedCards] = useState(new Set());
  const limit = 20;
  
  // Only pass search parameters when we've actually searched
  const searchName = hasSearched ? nameSearch : '';
  const searchKeyword = hasSearched ? keywordSearch : '';
  const searchPublication = hasSearched ? publicationSearch : '';
  
  const { data, isLoading, isFetching } = useEditors(limit, offset, searchName, searchKeyword, searchPublication, true);
  
  const editors = data?.editors || [];
  const totalCount = data?.totalCount || 0;
  const hasMore = offset + limit < totalCount;
  
  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0); // Reset to first page when searching
    setHasSearched(true);
    // The query will automatically refetch when the search parameters change
    // due to the queryKey dependency on the search values
  };

  const clearAllSearches = () => {
    setNameSearch('');
    setKeywordSearch('');
    setPublicationSearch('');
    setOffset(0);
    setHasSearched(false);
  };

  const hasActiveSearch = nameSearch || keywordSearch || publicationSearch;

  const toggleCard = (editorId) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(editorId)) {
      newExpanded.delete(editorId);
    } else {
      newExpanded.add(editorId);
    }
    setExpandedCards(newExpanded);
  };

  const loadMore = () => {
    setOffset(prev => prev + limit);
  };
  
  const loadPrevious = () => {
    setOffset(prev => Math.max(0, prev - limit));
  };
  
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const formatAreas = (text) => {
    if (!text) return '';
    // Add space after commas if not already present
    return text.replace(/,(?!\s)/g, ', ');
  };

  const highlightSearchTerms = (text) => {
    if (!text) return text;
    let highlightedText = text;
    
    // Highlight name search terms
    if (nameSearch) {
      const nameRegex = new RegExp(`(${nameSearch})`, 'gi');
      highlightedText = highlightedText.replace(nameRegex, '<mark style="background-color: #dbeafe; padding: 0.125rem 0.25rem; border-radius: 0.25rem;">$1</mark>');
    }
    
    // Highlight keyword search terms
    if (keywordSearch) {
      const keywordRegex = new RegExp(`(${keywordSearch})`, 'gi');
      highlightedText = highlightedText.replace(keywordRegex, '<mark style="background-color: #fef3c7; padding: 0.125rem 0.25rem; border-radius: 0.25rem;">$1</mark>');
    }
    
    // Highlight publication search terms
    if (publicationSearch) {
      const publicationRegex = new RegExp(`(${publicationSearch})`, 'gi');
      highlightedText = highlightedText.replace(publicationRegex, '<mark style="background-color: #dcfce7; padding: 0.125rem 0.25rem; border-radius: 0.25rem;">$1</mark>');
    }
    
    return highlightedText;
  };

  // Check if user has access (Professional or Premium only)
  const hasAccess = subscriptionPlan === 'professional' || subscriptionPlan === 'premium';
  const isBasic = subscriptionPlan === 'basic';

  if (subscriptionLoading || (isLoading && offset === 0)) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  // Show upgrade message for Basic subscribers
  if (isBasic) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          padding: '3rem 2rem',
          textAlign: 'center',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
            Editor Database
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#4b5563', marginBottom: '2rem', lineHeight: '1.6' }}>
            The Editor Database is available to Professional and Premium subscribers only.
          </p>
          <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '2rem' }}>
            Upgrade your subscription to access our comprehensive database of editors and their contact information.
          </p>
          <button
            onClick={() => navigate('/plans')}
            style={{
              backgroundColor: '#38b44a',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
          >
            Upgrade to Professional or Premium
          </button>
        </div>
      </div>
    );
  }

  // Show access denied if no subscription or subscription loading failed
  if (!hasAccess && !subscriptionLoading) {
    return (
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          padding: '3rem 2rem',
          textAlign: 'center',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
        }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111827', marginBottom: '1rem' }}>
            Editor Database
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#4b5563', marginBottom: '2rem', lineHeight: '1.6' }}>
            The Editor Database is available to Professional and Premium subscribers only.
          </p>
          <button
            onClick={() => navigate('/plans')}
            style={{
              backgroundColor: '#38b44a',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '6px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f3a'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#38b44a'}
          >
            View Subscription Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem 1rem' }}>
      <style>
        {`
          @media (max-width: 768px) {
            .search-field-container {
              margin-bottom: 0 !important;
            }
            .search-fields-flex {
              gap: 1rem !important;
            }
            .search-button-container {
              margin-top: 10px !important;
            }
          }
        `}
      </style>
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: '#111827', marginBottom: '0.5rem' }}>
          Editor Database
        </h1>
        {hasActiveSearch && (
          <p style={{ color: '#4b5563' }}>
            {isFetching ? 'Searching...' : `Showing ${editors.length} of ${totalCount} editors`}
          </p>
        )}
      </div>

      {/* Search Form */}
      <div style={{ 
        backgroundColor: 'white', 
        border: '1px solid #e5e7eb', 
        borderRadius: '0.5rem', 
        padding: '1.5rem', 
        marginBottom: '2rem',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          <div className="search-fields-flex" style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem' }}>
            <div className="search-field-container" style={{ flex: '1 1 200px', minWidth: '200px', marginBottom: '0.5rem' }}>
              <label htmlFor="publicationSearch" style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                🏢 Publication
              </label>
              <input 
                type="text" 
                id="publicationSearch" 
                style={{
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  outline: 'none',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Search by publication..."
                value={publicationSearch}
                onChange={(e) => setPublicationSearch(e.target.value)}
              />
            </div>
            <div className="search-field-container" style={{ flex: '1 1 200px', minWidth: '200px', marginBottom: '0.5rem' }}>
              <label htmlFor="nameSearch" style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                🔍 Editor Name
              </label>
              <input 
                type="text" 
                id="nameSearch" 
                style={{
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  outline: 'none',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Search by editor name or title..."
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
              />
            </div>
            <div className="search-field-container" style={{ flex: '1 1 200px', minWidth: '200px', marginBottom: '0.5rem' }}>
              <label htmlFor="keywordSearch" style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: '#374151', marginBottom: '0.5rem' }}>
                🏷️ Keywords
              </label>
              <input 
                type="text" 
                id="keywordSearch" 
                style={{
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  outline: 'none',
                  fontSize: '0.875rem',
                  boxSizing: 'border-box'
                }}
                placeholder="Search areas, categories, or topics..."
                value={keywordSearch}
                onChange={(e) => setKeywordSearch(e.target.value)}
              />
            </div>
            <div className="search-field-container search-button-container" style={{ flex: '1 1 200px', minWidth: '200px', display: 'flex', alignItems: 'end', marginBottom: '0.5rem' }}>
              <button 
                type="submit" 
                disabled={!hasActiveSearch || isFetching}
                style={{
                  width: '100%',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: hasActiveSearch && !isFetching ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: hasActiveSearch && !isFetching ? 'pointer' : 'not-allowed',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => {
                  if (hasActiveSearch && !isFetching) {
                    e.target.style.backgroundColor = '#2563eb';
                  }
                }}
                onMouseOut={(e) => {
                  if (hasActiveSearch && !isFetching) {
                    e.target.style.backgroundColor = '#3b82f6';
                  }
                }}
              >
                {isFetching ? '⏳ Searching...' : '🔍 Search'}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {publicationSearch && (
                <span style={{ 
                  fontSize: '0.75rem', 
                  backgroundColor: '#dcfce7', 
                  color: '#166534', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  Publication: {publicationSearch}
                  <button 
                    type="button"
                    onClick={() => setPublicationSearch('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    ×
                  </button>
                </span>
              )}
              {nameSearch && (
                <span style={{ 
                  fontSize: '0.75rem', 
                  backgroundColor: '#dbeafe', 
                  color: '#1e40af', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  Name: {nameSearch}
                  <button 
                    type="button"
                    onClick={() => setNameSearch('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    ×
                  </button>
                </span>
              )}
              {keywordSearch && (
                <span style={{ 
                  fontSize: '0.75rem', 
                  backgroundColor: '#fef3c7', 
                  color: '#92400e', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  Keywords: {keywordSearch}
                  <button 
                    type="button"
                    onClick={() => setKeywordSearch('')}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem' }}
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
            {hasActiveSearch && (
              <button 
                type="button"
                onClick={clearAllSearches}
                style={{
                  fontSize: '0.875rem',
                  color: '#2563eb',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline'
                }}
                onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
                onMouseOut={(e) => e.target.style.color = '#2563eb'}
              >
                Clear all
              </button>
            )}
          </div>
        </form>
      </div>

      {editors.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem 0' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', color: '#374151', marginBottom: '0.5rem' }}>
            {hasSearched ? 'No editors found' : 'Loading editors...'}
          </h3>
          <p style={{ color: '#6b7280', fontSize: '1.125rem', marginBottom: '1rem' }}>
            {hasSearched ? 'No editors match your search criteria' : 'Please wait while we load the editor database'}
          </p>
          <button 
            onClick={clearAllSearches}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
          >
            Clear all searches
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {editors.map((editor) => {
            const isExpanded = expandedCards.has(editor.id);
            return (
              <div key={editor.id} className="editor-database-card" style={{ 
                backgroundColor: 'white', 
                border: '1px solid #e5e7eb', 
                borderRadius: '0.5rem', 
                padding: '1rem',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                cursor: isExpanded ? 'default' : 'pointer',
                transition: 'all 0.2s ease'
              }}
              onClick={() => { if (!isExpanded) toggleCard(editor.id); }}
              >
                {/* Mobile: Top row with name and position */}
                <div className="editor-mobile-top-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.5rem' }}>
                    <h3 
                      className="editor-name-mobile"
                      style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 }}
                      dangerouslySetInnerHTML={{
                        __html: highlightSearchTerms(editor.name || 'Unnamed Editor')
                      }}
                    ></h3>
                    {editor.title && (
                      <>
                        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>•</span>
                        <span 
                          className="editor-title-mobile"
                          style={{ fontSize: '0.875rem', color: '#6b7280' }}
                          dangerouslySetInnerHTML={{
                            __html: highlightSearchTerms(editor.title)
                          }}
                        ></span>
                      </>
                    )}
                  </div>
                  {editor.company && (
                    <span 
                      className="editor-company-mobile"
                      style={{ 
                        fontSize: '0.85rem',
                        color: 'white',
                        fontWeight: '600',
                        backgroundColor: '#38b44a',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        display: 'inline-block'
                      }}
                      dangerouslySetInnerHTML={{
                        __html: highlightSearchTerms(editor.company)
                      }}
                    ></span>
                  )}
                </div>

                {/* Horizontal Table Layout */}
                <div className="editor-card-grid" style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '1rem', justifyContent: 'space-between' }}>
                  {/* Column 1: Name, Position, Company */}
                  <div className="editor-name-section" style={{ flex: '1 1 0', minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                      <h3 
                        style={{ fontSize: '1rem', fontWeight: '600', color: '#111827', margin: 0 }}
                        dangerouslySetInnerHTML={{
                          __html: highlightSearchTerms(editor.name || 'Unnamed Editor')
                        }}
                      ></h3>
                      {editor.title && (
                        <span 
                          style={{ fontSize: '0.875rem', color: '#6b7280' }}
                          dangerouslySetInnerHTML={{
                            __html: highlightSearchTerms(editor.title)
                          }}
                        ></span>
                      )}
                    </div>
                    {editor.company && (
                      <span 
                        style={{ 
                          fontSize: '0.85rem',
                          color: 'white',
                          fontWeight: '600',
                          backgroundColor: '#38b44a',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          display: 'inline-block'
                        }}
                        dangerouslySetInnerHTML={{
                          __html: highlightSearchTerms(editor.company)
                        }}
                      ></span>
                    )}
                  </div>
                  
                  {/* Column 2: Areas */}
                  <div>
                    {editor.areas && (
                      <div style={{ fontSize: '0.875rem', color: '#4b5563' }}>
                        {truncateText(formatAreas(editor.areas), 150)}
                      </div>
                    )}
                  </div>
                  
                  {/* Column 3: Categories */}
                  <div className={`editor-categories ${isExpanded ? 'expanded' : 'collapsed'}`}>
                    {editor.categories && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {editor.categories.split(',').slice(0, 3).map((category, index) => {
                          const trimmedCategory = category.trim();
                          if (!trimmedCategory) return null;
                          return (
                            <span
                              key={index}
                              style={{
                                fontSize: '0.75rem',
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '9999px',
                                border: '1px solid #e5e7eb',
                                display: 'inline-block'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: highlightSearchTerms(trimmedCategory)
                              }}
                            ></span>
                          );
                        })}
                        {editor.categories.split(',').length > 3 && (
                          <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                            +{editor.categories.split(',').length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Column 4: Expand Arrow */}
                  <div
                    className="editor-expand-arrow"
                    style={{ 
                      fontSize: '1.25rem', 
                      color: '#6b7280',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s ease',
                      textAlign: 'center',
                      cursor: 'pointer'
                    }}
                    onClick={(e) => { e.stopPropagation(); toggleCard(editor.id); }}
                  >
                    ▼
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div style={{ 
                    marginTop: '1rem', 
                    paddingTop: '1rem', 
                    borderTop: '1px solid #f3f4f6',
                    animation: 'fadeIn 0.2s ease-in'
                  }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
                      
                    </div>
                    
                    {editor.whatToPitch && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>What to Pitch</span>
                        <div 
                          style={{ fontSize: '0.875rem', color: '#4b5563', margin: '0.25rem 0 0 0', whiteSpace: 'pre-wrap' }}
                          dangerouslySetInnerHTML={{
                            __html: highlightSearchTerms(editor.whatToPitch)
                          }}
                        ></div>
                        <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb', margin: '0.75rem 0' }} />
                      </div>
                    )}
                    
                    {editor.pitchGuide && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Style Guide</span>
                        <div style={{ margin: '0.25rem 0 0 0' }}>
                          <a 
                            href={editor.pitchGuide} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{
                              fontSize: '0.875rem',
                              color: '#38b44a',
                              textDecoration: 'none',
                              fontWeight: '500',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            View Style Guide
                            <span style={{ fontSize: '0.75rem' }}>↗</span>
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {editor.rates && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Rates</span>
                        <p style={{ fontSize: '0.875rem', color: '#4b5563', margin: '0.25rem 0 0 0', whiteSpace: 'pre-wrap' }}>{editor.rates}</p>
                      </div>
                    )}
                    
                    {editor.comments && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Comments</span>
                        <p style={{ fontSize: '0.875rem', color: '#4b5563', margin: '0.25rem 0 0 0', whiteSpace: 'pre-wrap' }}>{editor.comments}</p>
                      </div>
                    )}
                    
                    {editor.email && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#374151', textTransform: 'uppercase' }}>Email</span>
                        <p style={{ fontSize: '0.875rem', color: '#4b5563', margin: '0.25rem 0 0 0' }}>{editor.email}</p>
                      </div>
                    )}
                    
                    {/* Comments Section */}
                    <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                      <Comments 
                        contentId={editor.id} 
                        contentType="editor"
                        countOnly={true}
                      />
                    </div>
                    
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalCount > limit && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '2rem' }}>
          <button
            onClick={loadPrevious}
            disabled={offset === 0}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: offset === 0 ? 'not-allowed' : 'pointer',
              backgroundColor: offset === 0 ? '#d1d5db' : '#3b82f6',
              color: offset === 0 ? '#6b7280' : 'white'
            }}
            onMouseOver={(e) => {
              if (offset !== 0) e.target.style.backgroundColor = '#2563eb';
            }}
            onMouseOut={(e) => {
              if (offset !== 0) e.target.style.backgroundColor = '#3b82f6';
            }}
          >
            Previous
          </button>
          
          <span style={{ color: '#4b5563' }}>
            {hasActiveSearch 
              ? `Showing ${offset + 1} to ${Math.min(offset + limit, totalCount)} of ${totalCount} results`
              : `Page ${Math.floor(offset / limit) + 1}`
            }
          </span>
          
          <button
            onClick={loadMore}
            disabled={!hasMore}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: !hasMore ? 'not-allowed' : 'pointer',
              backgroundColor: !hasMore ? '#d1d5db' : '#3b82f6',
              color: !hasMore ? '#6b7280' : 'white'
            }}
            onMouseOver={(e) => {
              if (hasMore) e.target.style.backgroundColor = '#2563eb';
            }}
            onMouseOut={(e) => {
              if (hasMore) e.target.style.backgroundColor = '#3b82f6';
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default EditorDatabase;
